import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ProjectConfig, ResourceConfig } from "@super-gantt/engine";
import { personNames, projectConfigSchema } from "@super-gantt/core";
import { api, type ProjectDetail } from "../lib/api";

/** Project settings: calendar, people, and an advanced JSON editor for everything else. */
export function SettingsPage() {
  const { id } = useParams();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [cfg, setCfg] = useState<ProjectConfig | null>(null);
  const [name, setName] = useState("");
  const [json, setJson] = useState("");
  const [jsonMode, setJsonMode] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    const p = await api.getProject(id!);
    setProject(p); setCfg(p.config); setName(p.name); setJson(JSON.stringify(p.config, null, 2));
  };
  useEffect(() => { load().catch(e => setMsg({ ok: false, text: e.message })); }, [id]);

  const working: ProjectConfig | null = useMemo(() => {
    if (!jsonMode) return cfg;
    try { return JSON.parse(json); } catch { return null; }
  }, [jsonMode, json, cfg]);
  const issues = useMemo(() => {
    if (!working) return ["The JSON doesn't parse."];
    const r = projectConfigSchema.safeParse(working);
    return r.success ? [] : r.error.issues.slice(0, 8).map(i => `${i.path.join(".")}: ${i.message}`);
  }, [working]);

  if (!project || !cfg) return msg ? <p className="error">{msg.text}</p> : <p className="muted">Loading…</p>;

  const set = (patch: Partial<ProjectConfig>) => { const n = { ...cfg, ...patch }; setCfg(n); setJson(JSON.stringify(n, null, 2)); };
  const setPerson = (i: number, patch: Partial<ResourceConfig>) => {
    const resources = cfg.resources.map((r, j) => (j === i ? { ...r, ...patch } : r));
    set({ resources });
  };
  /** Edits first or last name and keeps the full name in step. */
  const setPersonName = (i: number, patch: { first?: string; last?: string }) => {
    const cur = personNames(cfg.resources[i]);
    const first = patch.first ?? cur.first, last = patch.last ?? cur.last;
    setPerson(i, { firstName: first, lastName: last || undefined, name: [first.trim(), last.trim()].filter(Boolean).join(" ") || first });
  };
  const teamsOf = (key: string) => cfg.teams.filter(t => t.members.includes(key)).map(t => t.id);
  const setTeams = (key: string, ids: number[]) =>
    set({ teams: cfg.teams.map(t => ({ ...t, members: ids.includes(t.id) ? [...new Set([...t.members, key])] : t.members.filter(m => m !== key) })) });

  const save = async () => {
    setMsg(null);
    try {
      if (name !== project.name) await api.renameProject(project.id, name);
      await api.putConfig(project.id, working!);
      setMsg({ ok: true, text: "Saved. Open boards refresh automatically." });
      await load();
    } catch (e: any) {
      const details = Array.isArray(e.body?.details) ? " " + e.body.details.slice(0, 3).map((d: any) => `${(d.path || []).join(".")}: ${d.message}`).join("; ") : "";
      setMsg({ ok: false, text: e.message + details });
    }
  };

  const addPerson = () => {
    const key = uniqueKey(cfg.resources.map(r => r.key), "New person");
    set({ resources: [...cfg.resources, { key, name: key, discipline: cfg.disciplines[0].key, fte: 1 }] });
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h1>{project.name} · settings</h1>
        <Link to={`/p/${project.id}`}>Open board →</Link>
      </div>

      <div className="card">
        <div className="row">
          <label className="field" style={{ flex: 2, minWidth: 220 }}>Project name<input value={name} onChange={e => setName(e.target.value)} /></label>
          <label className="field" style={{ flex: 1, minWidth: 140 }}>First workday<input type="date" value={cfg.startDate} disabled={jsonMode} onChange={e => set({ startDate: e.target.value })} /></label>
          <label className="field" style={{ width: 110 }}>Workdays / week<input type="number" min={1} max={7} value={cfg.workdaysPerWeek} disabled={jsonMode} onChange={e => set({ workdaysPerWeek: +e.target.value })} /></label>
          <label className="field" style={{ width: 110 }}>Runway weeks<input type="number" min={1} value={cfg.runwayWeeks} disabled={jsonMode} onChange={e => set({ runwayWeeks: +e.target.value })} /></label>
          <label className="field" style={{ width: 120 }}>Default utilization<input type="number" step={0.05} min={0.05} max={1} value={cfg.defaultUtilization} disabled={jsonMode} onChange={e => set({ defaultUtilization: +e.target.value })} /></label>
        </div>
      </div>

      <h2>Display</h2>
      <div className="card">
        <label className="field" style={{ maxWidth: 320, marginBottom: 4 }}>Display name
          <select value={cfg.display?.personName ?? "first"} disabled={jsonMode}
            onChange={e => set({ display: { ...cfg.display, personName: e.target.value as "first" | "last" } })}>
            <option value="first">First name</option>
            <option value="last">Last name</option>
          </select>
        </label>
        <div className="muted" style={{ fontSize: 13 }}>What the board shows for each person. A nickname, when set, is shown instead.</div>
      </div>

      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2>People</h2>
        <label className="row muted" style={{ fontSize: 13 }}>
          <input type="checkbox" checked={jsonMode} onChange={e => { setJsonMode(e.target.checked); if (!e.target.checked && working) setCfg(working); }} />
          Edit everything as JSON (teams, disciplines, priorities, name aliases)
        </label>
      </div>

      {jsonMode ? (
        <textarea value={json} onChange={e => setJson(e.target.value)} spellCheck={false} style={{ width: "100%", minHeight: 520 }} />
      ) : (
        <div className="card overflow">
          <table className="list">
            <thead><tr><th>First name</th><th>Last name</th><th>Nickname</th><th>Discipline</th><th>FTE</th><th>Utilization</th><th>Starts after (weeks)</th><th>Teams</th></tr></thead>
            <tbody>
              {cfg.resources.map((r, i) => (
                <tr key={r.key}>
                  <td><input value={personNames(r).first} onChange={e => setPersonName(i, { first: e.target.value })} /></td>
                  <td><input value={personNames(r).last} onChange={e => setPersonName(i, { last: e.target.value })} /></td>
                  <td style={{ width: 120 }}><input value={r.nickname ?? ""} placeholder="—" onChange={e => setPerson(i, { nickname: e.target.value || undefined })} /></td>
                  <td>
                    <select value={r.discipline} onChange={e => setPerson(i, { discipline: e.target.value })}>
                      {cfg.disciplines.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                  </td>
                  <td style={{ width: 80 }}><input type="number" step={0.1} min={0.1} max={1} value={r.fte} onChange={e => setPerson(i, { fte: +e.target.value })} /></td>
                  <td style={{ width: 100 }}><input type="number" step={0.05} min={0.05} max={1} placeholder={String(cfg.defaultUtilization)} value={r.defaultUtilization ?? ""} onChange={e => setPerson(i, { defaultUtilization: e.target.value === "" ? undefined : +e.target.value })} /></td>
                  <td style={{ width: 110 }}><input type="number" min={0} value={r.offsetWeeks ?? ""} placeholder="0" onChange={e => setPerson(i, { offsetWeeks: e.target.value === "" ? undefined : +e.target.value })} /></td>
                  <td>
                    <select multiple value={teamsOf(r.key).map(String)} style={{ minHeight: 30 }}
                      onChange={e => setTeams(r.key, [...e.target.selectedOptions].map(o => +o.value))}>
                      {cfg.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn" onClick={addPerson}>Add person</button>
            <span className="muted" style={{ fontSize: 13 }}>Removing someone, renaming teams and editing disciplines are in the JSON editor for now.</span>
          </div>
        </div>
      )}

      {issues.length > 0 && <div className="error" style={{ marginTop: 10 }}>{issues.map(i => <div key={i}>{i}</div>)}</div>}
      <div className="row" style={{ marginTop: 16 }}>
        <button className="btn primary" disabled={issues.length > 0} onClick={save}>Save settings</button>
        {msg && <span className={msg.ok ? "ok" : "error"}>{msg.text}</span>}
      </div>
    </>
  );
}

function uniqueKey(existing: string[], base: string) {
  let k = base, n = 2;
  while (existing.includes(k)) k = `${base} ${n++}`;
  return k;
}
